// ---------------------------------------------------------------------------
// An arbitrary CSV as SOURCING CONSTRAINTS ("Weitere Felder vom CSV")
//
// A customer's file is often not a lead list at all: it is companies, sites,
// regions — whatever their CRM happened to export. `lead-csv.ts` reads the one
// shape we can turn straight into leads (it requires an email or a LinkedIn
// URL); this module reads the other case, where the file describes WHO TO LOOK
// FOR rather than who to contact.
//
// Three rules shape it, and each one exists because the obvious alternative is
// quietly wrong:
//
// 1. VALUES ARE NEVER INVENTED. An LLM classifies a COLUMN — its header, shown
//    a few sample values — into one of the slots below. The values themselves
//    pass through verbatim. Asking a model to emit the customer's company list
//    invites it to paraphrase "Migros Genossenschaftsbund" or drop the tail of
//    a long list, and a silently shortened company list looks exactly like a
//    correct one.
//
// 2. AN UNCONSTRAINED DIMENSION IS NEVER ENUMERATED. A dimension counts as
//    constrained only when something actually supplied values for it. With no
//    company column there must be no company list on screen and no company
//    filter in the request — not every company Apollo knows.
//    `constrainedDimensions` is that rule, written once.
//
// 3. A COLUMN WE CANNOT USE IS SHOWN, NOT DROPPED. Anything that fits no slot
//    stays visible as `unmapped`. The operator uploaded it and would otherwise
//    reasonably assume it narrowed the search (the no-silent-defaults rule).
//
// The slot list is deliberately closed, and short. Every entry is a free-text
// list filter Apollo genuinely accepts, so a value can travel from the file to
// the request untouched. Two tempting slots are deliberately absent:
//   • departments — the UI field is a fixed 10-value dropdown and the server
//     drops anything outside it, so a CSV column of free-text departments would
//     vanish silently. Such a column reads better as titles, or as unmapped.
//   • employee counts — Apollo wants "min,max" buckets, so a column of raw
//     headcounts would have to be bucketed, and bucketing is a judgement the
//     file did not make. Unmapped is the honest answer.
// ---------------------------------------------------------------------------

/** Sourcing dimensions a CSV column can fill. Closed set — see the header. */
export const CSV_FIELD_SLOTS = [
  'organizationNames',
  'organizationLocations',
  'personLocations',
  'personTitles',
  'organizationIndustryKeywords',
] as const;

export type CsvFieldSlot = (typeof CSV_FIELD_SLOTS)[number];

/** A column that fits no sourcing slot: shown to the operator, never sent. */
export const UNMAPPED_SLOT = 'unmapped';

export type CsvColumnSlot = CsvFieldSlot | typeof UNMAPPED_SLOT;

const SLOT_SET: ReadonlySet<string> = new Set<string>(CSV_FIELD_SLOTS);

/** Human labels for the slots, used by the UI and by nothing else. */
export const CSV_FIELD_SLOT_LABELS: Record<CsvFieldSlot, string> = {
  organizationNames: 'Companies',
  organizationLocations: 'Company locations',
  personLocations: 'Contact locations',
  personTitles: 'Job titles',
  organizationIndustryKeywords: 'Industry keywords',
};

/** Distinct values kept per column. Beyond this the list is truncated, loudly. */
export const MAX_VALUES_PER_COLUMN = 200;

/** One column of an uploaded file, read without assuming what it means. */
export interface CsvColumn {
  /** Position in the file — the stable key, since headers can repeat. */
  index: number;
  /** Header exactly as written, trimmed. */
  header: string;
  /** Distinct non-empty values, first-seen order, original casing. */
  values: string[];
  /** Rows that carried any value for this column, before de-duplication. */
  filled: number;
  /** True when the column had more distinct values than the cap allows. */
  truncated: boolean;
}

/** A column once it has been assigned a slot and the operator has pruned it. */
export interface CsvSourcingField extends CsvColumn {
  slot: CsvColumnSlot;
  /** The values still active. Starts as `values`; the operator removes from it. */
  selected: string[];
}

/**
 * Read every column of a parsed sheet, making no assumption about which ones
 * matter. Columns with a blank header are skipped: an unnameable column cannot
 * be classified, shown or reasoned about. Repeated headers are NOT merged —
 * two columns called "Location" may hold genuinely different data, and showing
 * both is truthful where silently unioning them is a guess.
 */
export function readCsvColumns(rows: readonly (readonly string[])[], maxValues = MAX_VALUES_PER_COLUMN): CsvColumn[] {
  if (rows.length < 2) return [];
  const headers = rows[0] ?? [];
  const columns: CsvColumn[] = [];

  for (let c = 0; c < headers.length; c++) {
    const header = (headers[c] ?? '').replace(/['"]/g, '').trim();
    if (!header) continue;

    const values: string[] = [];
    const seen = new Set<string>();
    let filled = 0;
    let truncated = false;

    for (let r = 1; r < rows.length; r++) {
      const raw = (rows[r]?.[c] ?? '').trim();
      if (!raw) continue;
      filled++;
      const key = raw.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      // Keep counting `filled` past the cap — the operator should see that the
      // column is bigger than the list under it.
      if (values.length >= maxValues) {
        truncated = true;
        continue;
      }
      values.push(raw);
    }

    if (filled === 0) continue;
    columns.push({ index: c, header, values, filled, truncated });
  }

  return columns;
}

/** What the classifier is asked about one column — header plus a little evidence. */
export interface CsvColumnSample {
  index: number;
  header: string;
  sampleValues: string[];
  distinctValues: number;
}

/**
 * Compact description of the columns for the classifier. Only a few values per
 * column travel: the header plus a handful of examples is what decides the
 * slot, and sending thousands of company names would cost tokens to tell it
 * nothing more.
 */
export function describeColumnsForClassifier(columns: readonly CsvColumn[], sampleSize = 5): CsvColumnSample[] {
  return columns.map(column => ({
    index: column.index,
    header: column.header,
    sampleValues: column.values.slice(0, Math.max(0, sampleSize)),
    distinctValues: column.values.length,
  }));
}

/** Outcome of validating whatever the classifier returned. */
export interface NormalizedClassification {
  fields: CsvSourcingField[];
  /**
   * Slot strings the classifier produced that are not in our closed set. The
   * affected columns fall back to `unmapped`; the caller must LOG these rather
   * than shrug, because a drifting model contract is invisible otherwise.
   */
  unknownSlots: string[];
}

/**
 * Turn raw classifier output into fields, trusting none of it. A column the
 * classifier forgot, or gave a slot we do not know, becomes `unmapped` — the
 * visible, harmless answer — never a guessed slot. Values are taken from the
 * parsed column, never from the classifier's echo of them.
 */
export function normalizeClassifications(columns: readonly CsvColumn[], raw: unknown): NormalizedClassification {
  const byIndex = new Map<number, string>();
  const unknownSlots: string[] = [];

  const entries = Array.isArray(raw) ? raw : [];
  for (const entry of entries) {
    if (!entry || typeof entry !== 'object') continue;
    const record = entry as { index?: unknown; slot?: unknown };
    const index = typeof record.index === 'number' ? record.index : Number.NaN;
    if (!Number.isInteger(index)) continue;
    const slot = typeof record.slot === 'string' ? record.slot.trim() : '';
    if (!slot) continue;
    if (!SLOT_SET.has(slot) && slot !== UNMAPPED_SLOT) {
      unknownSlots.push(slot);
      continue;
    }
    byIndex.set(index, slot);
  }

  const fields = columns.map(column => {
    const slot = (byIndex.get(column.index) ?? UNMAPPED_SLOT) as CsvColumnSlot;
    return { ...column, slot, selected: [...column.values] };
  });

  return { fields, unknownSlots: [...new Set(unknownSlots)] };
}

/**
 * The dimensions that are actually constrained right now — the only ones that
 * may be shown as a value list or sent to Apollo.
 *
 * A dimension is constrained when at least one value survives for it. A column
 * the operator emptied is NOT a constraint: removing every company must widen
 * the search back to "any company", not narrow it to nothing. Several columns
 * can feed one dimension, so values are unioned, de-duplicated case-insensitively
 * and kept in first-seen order.
 */
export function constrainedDimensions(fields: readonly CsvSourcingField[]): Map<CsvFieldSlot, string[]> {
  const bySlot = new Map<CsvFieldSlot, string[]>();
  const seenBySlot = new Map<CsvFieldSlot, Set<string>>();

  for (const field of fields) {
    if (field.slot === UNMAPPED_SLOT) continue;
    const slot = field.slot;
    for (const value of field.selected) {
      const trimmed = value.trim();
      if (!trimmed) continue;
      const seen = seenBySlot.get(slot) ?? new Set<string>();
      const key = trimmed.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      seenBySlot.set(slot, seen);
      const list = bySlot.get(slot) ?? [];
      list.push(trimmed);
      bySlot.set(slot, list);
    }
  }

  return bySlot;
}

/** The sourcing-parameter shape this overlay contributes to. */
export interface CsvSourcingOverlay {
  organizationNames: string[];
  companyLocation: string[];
  personLocation: string[];
  jobTitle: string[];
  industryKeywords: string[];
}

const EMPTY_OVERLAY: CsvSourcingOverlay = {
  organizationNames: [],
  companyLocation: [],
  personLocation: [],
  jobTitle: [],
  industryKeywords: [],
};

/**
 * Project the constrained dimensions onto the sourcing form's own field names.
 * A dimension that is not constrained contributes an empty list, which the
 * caller must treat as "say nothing about this", never as "match nothing".
 */
export function csvSourcingOverlay(fields: readonly CsvSourcingField[]): CsvSourcingOverlay {
  const bySlot = constrainedDimensions(fields);
  return {
    ...EMPTY_OVERLAY,
    organizationNames: bySlot.get('organizationNames') ?? [],
    companyLocation: bySlot.get('organizationLocations') ?? [],
    personLocation: bySlot.get('personLocations') ?? [],
    jobTitle: bySlot.get('personTitles') ?? [],
    industryKeywords: bySlot.get('organizationIndustryKeywords') ?? [],
  };
}

/** Split a comma-separated form field ("CEO, CTO") into its terms. */
export function splitCommaList(value: string | undefined | null): string[] {
  return (value ?? '')
    .split(',')
    .map(part => part.trim())
    .filter(part => part.length > 0);
}

/** Union two term lists, case-insensitive, keeping the first spelling seen. */
export function unionTerms(existing: readonly string[], added: readonly string[]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const value of [...existing, ...added]) {
    const trimmed = value.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(trimmed);
  }
  return out;
}

/** The subset of the sourcing form the CSV overlay can touch. */
export interface CsvMergeableParams {
  organizationNames?: string[];
  companyLocation: string[];
  personLocation: string[];
  jobTitle: string;
  industryKeywords: string;
}

/**
 * Union the CSV's constrained dimensions into the sourcing form.
 *
 * UNION, not replace: the file and the form are two statements of the same
 * intent, and Apollo treats several values in one filter as "any of these", so
 * adding the file's companies to a company the user typed widens that ONE
 * dimension while every other dimension still narrows the search. Replacing
 * would silently discard what the operator typed.
 *
 * An unconstrained dimension leaves its field exactly as it was — the empty
 * overlay list means "the file says nothing here", never "match nothing".
 */
export function mergeCsvOverlay<T extends CsvMergeableParams>(params: T, overlay: CsvSourcingOverlay): T {
  return {
    ...params,
    organizationNames: unionTerms(params.organizationNames ?? [], overlay.organizationNames),
    companyLocation: unionTerms(params.companyLocation, overlay.companyLocation),
    personLocation: unionTerms(params.personLocation, overlay.personLocation),
    jobTitle: unionTerms(splitCommaList(params.jobTitle), overlay.jobTitle).join(', '),
    industryKeywords: unionTerms(splitCommaList(params.industryKeywords), overlay.industryKeywords).join(', '),
  };
}
